import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Destroyable, Rental, SaveForm, untilDestroy } from '@tokeet/tv3-platform'
import { Component, Inject, OnInit } from '@angular/core'
import { Store } from '@ngrx/store'
import { GetRooms, UpsertRooms } from '../../store/rooms/rooms.actions'
import { Room, RoomType, isBedsInRoom } from '../../store/rooms/rooms.model'
import * as R from 'ramda'
import { selectRooms } from '../../store/rooms/rooms.selectors'
import { flatten, omit } from 'lodash'

@Component({
  selector: 'app-rooms-overlay',
  templateUrl: './rooms-overlay.component.html',
  styleUrls: ['./rooms-overlay.component.scss'],
})
export class RoomsOverlayComponent extends Destroyable implements OnInit {
  form = this.fb.group({
    rooms: this.fb.array([]),
  })

  get roomsArray() {
    return this.form.get('rooms') as FormArray
  }

  bathrooms: Room[] = []
  rooms: Room[] = []

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { rental: Rental },
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<RoomsOverlayComponent>,
    private store: Store<any>
  ) {
    super()
  }

  ngOnInit() {
    this.store.dispatch(GetRooms({ rentalId: this.data.rental.id }))

    this.store.pipe(selectRooms(this.data.rental.id), untilDestroy(this)).subscribe((rooms) => {
      this.rooms = rooms
      this.resetForm()
      if (R.isEmpty(rooms)) {
        this.addEmptyRoom()
      } else {
        this.buildForm(rooms)
      }
      this.bathrooms = rooms.filter((r) => r.type === RoomType.BATHROOM)
    })
  }

  resetForm() {
    do {
      this.roomsArray.removeAt(0)
    } while (this.roomsArray.length > 0)
  }

  buildForm(rooms: Room[]) {
    rooms.forEach((room) => {
      const roomGroup = this.fb.group({
        pkey: [room.pkey],
        name: [room.name, [Validators.required]],
        description: [room.description, [Validators.required]],
        bathroom: [room.bathroom],
        type: [room.type, [Validators.required]],
        sub_type: [room.sub_type, [Validators.required]],
        beds: this.fb.array([]),
        size: [room.size, [Validators.required]],
        size_metric: [room.size_metric || 'SQFT', [Validators.required]],
      })
      const bedsArray = roomGroup.get('beds') as FormArray
      R.forEachObjIndexed(
        (value, key) => {
          bedsArray.push(
            this.fb.group({
              bed: [key],
              count: [((value as string[]) || [])?.length || 1],
            })
          )
        },
        R.groupBy((bed) => bed, room.beds)
      )
      this.roomsArray.push(roomGroup)
    })
  }

  addEmptyRoom() {
    const room = this.fb.group({
      bathroom: [''],
      type: ['', [Validators.required]],
      sub_type: ['', [Validators.required]],
      name: ['', [Validators.required]],
      description: ['', [Validators.required]],
      size: ['', [Validators.required]],
      beds: this.fb.array([]),
      size_metric: ['SQFT', [Validators.required]],
    })
    this.roomsArray.push(room)
  }

  onRemoveRoom(index: number) {
    this.roomsArray.removeAt(index)
  }

  close() {
    this.dialogRef.close()
  }

  @SaveForm()
  onSave(form: FormGroup) {
    let createRooms = []
    let updateRooms = []
    for (const roomForm of this.roomsArray.controls) {
      const room = roomForm.value
      const beds = flatten(room.beds.map((bed) => R.times(() => bed.bed, bed.count)))
      const payload: Partial<Room> = {
        ...room,
        beds: isBedsInRoom(room.type, room.sub_type) ? beds : [],
      }
      if (payload.pkey) {
        updateRooms.push(payload)
      } else {
        createRooms.push({ ...omit(payload, 'pkey'), rental_id: this.data.rental.id })
      }
    }
    this.store.dispatch(UpsertRooms({ createRooms, updateRooms }))
  }
}
